import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { cloneDeep, filter, get, size, set, sortBy } from 'lodash';

import Button from '../../components/Button';
import Element from '../../components/element';
import Flex from '../../components/flex';
import defaultAssessments from '../../config/assessments';
import { colors } from '../../config/theme';
import {
  apiRequest,
  getActiveAssessments,
  setActiveAssessment,
  setActiveAssessments,
  setSortedAssessments,
  setSurveysLastModifiedOn
} from '../../root/actions';
import { ReactComponent as SurveyStatusSVG } from '../../assets/icons/survey_status.svg';
import { ReactComponent as IconInfo } from '../../assets/icons/IconInfo.svg';
import { isSurveyCompleted, isSurveyPartiallyCompleted } from '../../utils/survey-parser';
import { ASSESSMENTS_URL } from '../../api/endpoints';
import Signup from '../../containers/Signup';
import Modal from '../../components/modal';

const getReduxData = state => {
  return {
    activeAssessments: state.containers.app.get('activeAssessments'),
    activeAssessment: state.containers.app.get('activeAssessment'),
    sortedAssessments: state.containers.app.get('sortedAssessments'),
    surveysLastCompletedAt: state.containers.app.get('surveysLastCompletedAt'),
    surveysLastUpdatedAt: state.containers.app.get('surveysLastUpdatedAt'),
    surveysLastModifiedOn: state.containers.app.get('surveysLastModifiedOn'),
    user: state.containers.app.get('user'),
    prevVisits: state.containers.app.get('user')?.visits,
    recentVisit: state.containers.app.get('user')?.visits[0]
  };
};

const Assessments = () => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [surveyStatus, setSurveyStatus] = useState('');
  const [showScreen, setShowScreen] = useState(false);
  const [
    isUserCompletedSurveysWithIn30Days,
    setIsUserCompletedSurveysWithIn30Days
  ] = useState(null);
  const {
    activeAssessments,
    sortedAssessments,
    activeAssessment,
    surveysLastCompletedAt,
    surveysLastUpdatedAt,
    surveysLastModifiedOn,
    user,
    prevVisits,
    recentVisit
  } = useSelector(getReduxData);
  const { assessmentId } = useParams();

  const [isSurveyPartiallyUpdated, setIsSurveyPartiallyUpdated] = useState(
    null
  );

  const toggleProfileModal = () => {
    setShowProfileModal(!showProfileModal);
  };

  const isAllSurveysCompleted = () => {
    const totalSurveys = size(sortedAssessments);
    const completedSurveys =
      filter(sortedAssessments, assessment => assessment.completed).length ||
      0;

    // console.log("total **************** ", totalSurveys)
    // console.log("completedSurveys **************** ", completedSurveys)
    return completedSurveys == totalSurveys;
    // return '6/8';
  };

  const updateSurvey = allSurveys => {
    const request = {
      method: 'put',
      url: ASSESSMENTS_URL,
      data: allSurveys
    };
    const meta = {
      onSuccess: res => { }
    };
    dispatch(apiRequest(request, meta));
  };
  const dashboardContent = {
    isLastVisitWithin30Days: {
      header:
        'Before we can schedule you for a follow-up visit, we need you to complete the pre-visit assessments.',
      description:
        'We have information from your last visit. Please review and update your history, allergies, and medication if anything has changed since your last visit. Sleep Quality, Physical Activity, Nutrition, and Mental Health Assessments will require new answers.'
    },
    isLastVisitAfter30Days: {
      header:
        'Before we can schedule you for a follow-up visit, we need you to complete the pre-visit assessments.',
      description:
        'Because you didn’t schedule an appointment within 30 days of completing the pre-visit assessments, Sleep Quality, Physical Activity, Nutrition, and Mental Health Assessments will require new answers.'
    },
    firstTime: {
      header:
        'We need to get to know you a little better before you can schedule an appointment.',
      description: ''
    },
    partial: {
      header:
        'Welcome back! Please complete the remaining patient surveys so we can get your appointment scheduled.',
      description: ''
    }
  };

  const returnContent = type => (
    <>
      {type.header}
      {type.description.length > 0 && (
        <Flex marginTop="24px">
          <Element
            type={IconInfo}
            width="30px"
            height="25px"
            marginRight="7px"
          />
          <Element type="h5" fontSize="16px !important" fontFamily="GTAM">
            {type.description}
          </Element>
        </Flex>
      )}
    </>
  );

  const checkSurveyProgress = () => {
    let partiallyCompletedSurveys = filter(sortedAssessments, assessment =>
      isSurveyPartiallyCompleted(
        assessment,
        get(assessment, 'answers.raw[0]', {})
      )
    );
    if (
      partiallyCompletedSurveys.length &&
      partiallyCompletedSurveys.some(survey => !survey.completed) &&
      !isVisitExist
    ) {
      partiallyCompletedSurveys = sortBy(partiallyCompletedSurveys, ['order']);
      let nextSurvey = partiallyCompletedSurveys.find(
        survey => !survey.completed
      );
      dispatch(
        setActiveAssessment(
          nextSurvey ||
          partiallyCompletedSurveys[partiallyCompletedSurveys.length - 1]
        )
      );
      setSurveyStatus('partial');
    } else {
      const completedSurveys = filter(sortedAssessments, ['completed', true]);
      if (completedSurveys.length) {
        let nextSurvey = '';
        if (completedSurveys.length !== sortedAssessments.length) {
          const lastCompletedSurvey =
            completedSurveys[completedSurveys.length - 1];
          nextSurvey =
            sortedAssessments[
            lastCompletedSurvey.completed
              ? lastCompletedSurvey.order
              : lastCompletedSurvey.order - 1
            ];
          if (!nextSurvey) {
            for (let i = 0; i < sortedAssessments.length; i++) {
              if (
                sortedAssessments[i]?.title !== completedSurveys[i]?.title &&
                !nextSurvey
              ) {
                nextSurvey = sortedAssessments[i];
              }
            }
          }
          dispatch(setActiveAssessment(nextSurvey));
          setSurveyStatus('partial');
        } else {
          const shortName = window.location.pathname.split('/').slice(-1)[0];
          const activeAssessment = sortedAssessments.find(
            survey => survey.shortName === shortName
          );
          activeAssessment && dispatch(setActiveAssessment(activeAssessment));
          setSurveyStatus('completed');
        }
      } else {
        dispatch(setActiveAssessment(sortedAssessments?.[0]));
        setSurveyStatus('firstTime');
        setShowScreen(true)
      }
    }
  };

  const [
    isSurveyDataReceivedFromAPI,
    setIsSurveyDataReceivedFromAPI
  ] = useState(false);
  const pullAssessments = () => {
    dispatch(
      getActiveAssessments(
        {},
        {
          onSuccess: (result) => {
            setIsSurveyDataReceivedFromAPI(true);
          }
        }
      )
    );
  };

  useEffect(() => {
    if (!user.dob) {
      setShowProfileModal(true)
    }
    pullAssessments();
  }, []);

  useEffect(() => {
    if (!activeAssessments) {
      pullAssessments();
    } else {
      setIsSurveyDataReceivedFromAPI(false);
      checkSurveyProgress();
    }
  }, [activeAssessments]);

  const checkAnyCompletedVisit = () => {
    return prevVisits.some(visit => visit.status.completed);
  };

  const isSurveyCompletedAfterRecentAppointment = () => {
    if (prevVisits?.length) {
      const lastCompletedVisit = prevVisits?.find(
        visit => visit.status.completed
      );
      return moment(surveysLastCompletedAt).isAfter(
        lastCompletedVisit.completedDate
      );
    }
  };

  const alterAssessments = (
    firstFour = '',
    lastFour = '',
    startFromFirst = false,
    changeFirstFourToNotCompleted = true
  ) => {
    const currentDateTime = moment.utc().toISOString();
    const isModifiedRecently = surveysLastModifiedOn
      ? moment(surveysLastModifiedOn).isSameOrAfter(
        moment().subtract(30, 'days')
      )
      : false;
    const completedSurveysWithIn30Days = surveysLastCompletedAt
      ? moment(surveysLastCompletedAt).isSameOrAfter(
        moment().subtract(30, 'days')
      )
      : false;

    if (
      firstFour === 'old' &&
      lastFour === 'new' &&
      sortedAssessments &&
      (!isModifiedRecently || startFromFirst)
    ) {
      const defaultAssmts = cloneDeep(defaultAssessments);
      dispatch(
        setActiveAssessment(
          startFromFirst
            ? sortedAssessments?.[0]
            : Object.values(defaultAssmts)[4]
        )
      );
      let sortedAssessmentsTemp = sortedAssessments
        ? [...sortedAssessments]
        : [];
      const defaultAssessmentsValues = Object.values(defaultAssmts);
      if (!completedSurveysWithIn30Days) {
        for (let i = 4; i < sortedAssessments?.length; i++) {
          sortedAssessmentsTemp[i] = defaultAssessmentsValues[i];
          sortedAssessmentsTemp[i]['completed'] = true;
        }
      }
      if (changeFirstFourToNotCompleted && !completedSurveysWithIn30Days) {
        for (let i = 7; i > (!completedSurveysWithIn30Days ? 3 : 0); --i) {
          sortedAssessmentsTemp[i]['completed'] = false;
          sortedAssessmentsTemp[i].steps?.map(
            step => (step['completed'] = false)
          );
        }
      }
      const keys = [
        'familyHistory',
        'socialHistory',
        'medicalHistory',
        'medications',
        'sleep',
        'physicalActivity',
        'nutrition',
        'mentalHealth'
      ];
      let payload = {};
      for (let i = 0; i < 8; i++) {
        payload[keys[i]] = sortedAssessmentsTemp[i];
      }
      payload['surveysLastCompletedAt'] = surveysLastCompletedAt;
      payload['surveysLastModifiedOn'] = currentDateTime;
      //updateSurvey(payload); //TODO: need to R&D why its been used
      dispatch(setSortedAssessments(sortedAssessmentsTemp));
      dispatch(setSurveysLastModifiedOn(currentDateTime));
      setShowScreen(true)
    }
  };

  const [isVisitExist, setIsVisitExist] = useState(false);
  useEffect(() => {
    //check is any previous completed visits
    if (prevVisits?.length) {
      setIsVisitExist(checkAnyCompletedVisit());
    } else {
      setIsVisitExist(false);
    }
  }, [prevVisits]);

  useEffect(() => {
    //check is any previous completed visits
    if (sortedAssessments) {
      const check = filter(sortedAssessments, assessment =>
        location.pathname.includes(assessment.shortName)
      );
      if (check) {
        dispatch(setActiveAssessment(check[0]));
      }
    }
  }, [sortedAssessments]);

  useEffect(() => {
    //check is any previous completed visits
    if (activeAssessment === undefined) {
      dispatch(setActiveAssessment(sortedAssessments[0]));
    }
  }, [activeAssessment]);

  const [content, setContent] = useState(null);
  useEffect(() => {
    // this useEffect is to change the landing page according
    if (
      location.pathname === '/landing/assessments'
    ) {
      if (isVisitExist) {
        // booked and completed atleast one appointment already
        if (isSurveyCompletedAfterRecentAppointment()) {
          // follow up within 30 days of last survey completed
          if (isUserCompletedSurveysWithIn30Days && isAllSurveysCompleted()) {
            navigate('/landing/appointment/view');
          } else if (isUserCompletedSurveysWithIn30Days === false) {
            alterAssessments('old', 'new');
            setContent(
              returnContent(dashboardContent['isLastVisitAfter30Days'])
            );
          }
        } else if (isSurveyCompletedAfterRecentAppointment() === false) {
          const isUserUpdatedSurvey = localStorage.getItem(
            'isUserUpdatedSurvey'
          );
          if (isUserCompletedSurveysWithIn30Days && isAllSurveysCompleted()) {
            navigate('/landing/appointment/view');
          } else if (!isSurveyPartiallyUpdated && isUserUpdatedSurvey !== 'yes') {
            localStorage.removeItem('isUserUpdatedSurvey');
            alterAssessments('old', 'new', true, true);
          }
          setContent(
            returnContent(dashboardContent['isLastVisitWithin30Days'])
          );
        }
      } else {
        //fresh application (no appointments booked already or it(s) get cancelled)
        if (surveyStatus === 'firstTime') {
          setContent(returnContent(dashboardContent['firstTime']));
        } else if (surveyStatus === 'partial') {
          setContent(returnContent(dashboardContent['partial']));
        } else if (
          surveyStatus === 'completed' &&
          localStorage.getItem('isFinishSurveyBtnCLicked') !== 'yes'
        ) {
          if (isUserCompletedSurveysWithIn30Days) {
            navigate('/landing/appointment/view');
          } else if (isUserCompletedSurveysWithIn30Days === false) {
            alterAssessments('old', 'new');
            setContent(
              returnContent(dashboardContent['isLastVisitAfter30Days'])
            );
          }
        }
      }
    } else {
      alterAssessments('old', 'new');
    }
  }, [
    isVisitExist,
    surveyStatus,
    isUserCompletedSurveysWithIn30Days,
    isSurveyDataReceivedFromAPI,
    location.pathname
  ]);

  useEffect(() => {
    if (surveysLastCompletedAt || surveysLastUpdatedAt) {
      // checking last survey completion is within 30 days or not
      surveysLastCompletedAt &&
        setIsUserCompletedSurveysWithIn30Days(
          moment(surveysLastCompletedAt).isSameOrAfter(
            moment().subtract(30, 'days')
          )
        );
      setIsSurveyPartiallyUpdated(
        surveysLastUpdatedAt && surveysLastCompletedAt
          ? moment(surveysLastUpdatedAt).isAfter(surveysLastCompletedAt) && !isUserCompletedSurveysWithIn30Days
          : null
      );
    }
  }, [surveysLastCompletedAt, surveysLastUpdatedAt]);

  return (
    <>
      {!assessmentId && (
        <Element padding="0 15%" textAlign="center" height="100vh">
         {showScreen && <>
          <Element
            type={SurveyStatusSVG}
            width="64px"
            height="64px"
            marginBottom="16px"
          />

          <Element
            type="p"
            fontFamily="GTAR"
            fontSize="24px"
            color={colors.dustyGray}
            margin="16px 0 32px"
          >
            {content}
          </Element>
          <Button
            onClick={() =>
              activeAssessment != undefined
                ? navigate(activeAssessment._meta.route)
                : navigate('/landing/assessments/familyHistory')
            }
          >
            {surveyStatus === 'firstTime' || isSurveyPartiallyUpdated === false
              ? 'Begin Pre-Visit Assessments'
              : 'Continue Pre-Visit Assessment'}
          </Button>
          </>
          }
          {showProfileModal && (
            <Modal
              headerLabel="Edit Profile"
              closable
              shouldCloseOnOverlayClick
              preventScroll
              onClose={toggleProfileModal}
            >
              <Signup editable closeModal={toggleProfileModal} />
            </Modal>
          )}
        </Element>
      )}
      <Outlet />
    </>
  );
};

export default Assessments;
